import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, AlertTriangle, Filter, Settings, Zap, RotateCw, Droplets } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';
import Footer from '../components/Footer';
import UnifiedContactModal from '../components/UnifiedContactModal';

const CleaningOptionsPage = () => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Inline Filter Cleaning Options | Manual, Semi-Auto, Fully Automatic"
        description="Choose the right cleaning option for your BIG, BIG-S, or GNG inline filters. Start manual, upgrade to semi-automatic (BIF & MVC), or go fully automatic (RAB & AVC) for minimal downtime."
        canonicalPath="/filtration/inline/cleaning"
      />
      <Navbar />

      {/* Breadcrumbs */}
      <nav aria-label="Breadcrumb" className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <Link to="/" className="text-gray-600 hover:text-ctc-blue">Home</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li>
              <Link to="/filtration" className="text-gray-600 hover:text-ctc-blue">Filtration</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li>
              <Link to="/filtration/inline" className="text-gray-600 hover:text-ctc-blue">Inline Filters</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li className="text-gray-900 font-semibold">Cleaning Options</li>
          </ol>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-16 pb-12 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl font-bold text-gray-900 mb-4">
              Cleaning Options for Inline Filters
            </h1>
            <p className="text-2xl text-gray-600 mb-6 font-light leading-relaxed">
              Start manual, upgrade to semi-automatic, or go fully automatic to minimize downtime.
            </p>

            <div className="inline-flex items-center bg-orange-50 border border-orange-200 rounded-lg px-4 py-3 mb-8">
              <AlertTriangle className="text-orange-600 mr-2" size={20} />
              <span className="text-gray-700 font-medium">
                Applies to BIG, BIG-S, and GNG. TIG is not compatible.
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 3-Up Comparison Tiles */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Choose Your Cleaning Approach</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {/* Manual */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-lg shadow-xl border-2 border-gray-200 hover:border-ctc-blue transition-all overflow-hidden flex flex-col"
            >
              <div className="bg-gradient-to-r from-gray-600 to-gray-700 p-6">
                <Settings className="text-white mb-3" size={40} />
                <h3 className="text-2xl font-bold text-white">Manual</h3>
              </div>

              <div className="p-6 flex-1 flex flex-col">
                <p className="text-gray-700 mb-4 leading-relaxed">
                  Disassemble housing; remove and clean screen manually.
                </p>

                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Downtime:</span>
                    <span className="font-semibold text-red-600">Highest</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Labor:</span>
                    <span className="font-semibold text-red-600">Highest</span>
                  </div>
                </div>

                <p className="text-sm text-gray-600 mb-6">
                  <span className="font-semibold text-gray-900">Best for:</span> Lower duty cycles, budget-sensitive operations.
                </p>

                <div className="mt-auto">
                  <Link
                    to="/filtration/inline/cleaning/manual"
                    className="block w-full bg-gray-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-700 transition-colors text-center"
                  >
                    Manual Details
                  </Link>
                </div>
              </div>
            </motion.div>

            {/* Semi-Automatic */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-white rounded-lg shadow-xl border-2 border-blue-200 hover:border-ctc-blue transition-all overflow-hidden flex flex-col"
            >
              <div className="bg-gradient-to-r from-ctc-blue to-blue-600 p-6">
                <Filter className="text-white mb-3" size={40} />
                <h3 className="text-2xl font-bold text-white">Semi-Automatic</h3>
                <p className="text-blue-100 text-sm">(BIF & MVC)</p>
              </div>

              <div className="p-6 flex-1 flex flex-col">
                <p className="text-gray-700 mb-4 leading-relaxed">
                  Brush-in-filter and/or vacuum cleaning without full teardown.
                </p>

                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Downtime:</span>
                    <span className="font-semibold text-orange-600">Moderate</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Labor:</span>
                    <span className="font-semibold text-orange-600">Moderate</span>
                  </div>
                </div>

                <p className="text-sm text-gray-600 mb-6">
                  <span className="font-semibold text-gray-900">Best for:</span> Moderate loading, faster turnarounds.
                </p>

                <div className="mt-auto">
                  <Link
                    to="/filtration/inline/cleaning/semi-auto"
                    className="block w-full bg-ctc-blue text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors text-center"
                  >
                    Semi-Automatic (BIF & MVC)
                  </Link>
                </div>
              </div>
            </motion.div>

            {/* Fully Automatic */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white rounded-lg shadow-xl border-2 border-green-200 hover:border-green-600 transition-all overflow-hidden flex flex-col"
            >
              <div className="bg-gradient-to-r from-green-600 to-green-700 p-6">
                <Zap className="text-white mb-3" size={40} />
                <h3 className="text-2xl font-bold text-white">Fully Automatic</h3>
                <p className="text-green-100 text-sm">(RAB & AVC)</p>
              </div>

              <div className="p-6 flex-1 flex flex-col">
                <p className="text-gray-700 mb-4 leading-relaxed">
                  Brush and/or vacuum cycles triggered by ΔP/time.
                </p>

                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Downtime:</span>
                    <span className="font-semibold text-green-600">Lowest</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Labor:</span>
                    <span className="font-semibold text-green-600">Lowest</span>
                  </div>
                </div>

                <p className="text-sm text-gray-600 mb-6">
                  <span className="font-semibold text-gray-900">Best for:</span> High solids, 24/7 uptime, minimal operator intervention.
                </p>

                <div className="mt-auto">
                  <Link
                    to="/filtration/inline/cleaning/automatic"
                    className="block w-full bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors text-center"
                  >
                    Fully Automatic (RAB & AVC)
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Matrix Table */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Detailed Comparison Matrix</h2>

          <div className="overflow-x-auto bg-white rounded-lg shadow-lg">
            <table className="w-full border-collapse">
              <thead className="bg-gradient-to-r from-gray-700 to-gray-800 text-white">
                <tr>
                  <th className="px-4 py-4 text-left font-semibold">Feature</th>
                  <th className="px-4 py-4 text-left font-semibold">Manual</th>
                  <th className="px-4 py-4 text-left font-semibold">Semi-Auto (BIF & MVC)</th>
                  <th className="px-4 py-4 text-left font-semibold">Fully Auto (RAB & AVC)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold text-gray-900">Cleaning Action</td>
                  <td className="px-4 py-3 text-gray-700">Remove housing; clean screen externally</td>
                  <td className="px-4 py-3 text-gray-700">Brush/vacuum screen in place</td>
                  <td className="px-4 py-3 text-gray-700">Automated brush/vacuum cycles</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold text-gray-900">Trigger</td>
                  <td className="px-4 py-3 text-gray-700">Manual observation</td>
                  <td className="px-4 py-3 text-gray-700">Operator initiated</td>
                  <td className="px-4 py-3 text-gray-700">ΔP sensor / timer</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold text-gray-900">Typical Downtime</td>
                  <td className="px-4 py-3 text-red-600 font-semibold">30-60 minutes</td>
                  <td className="px-4 py-3 text-orange-600 font-semibold">5-15 minutes</td>
                  <td className="px-4 py-3 text-green-600 font-semibold">0 minutes (online)</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold text-gray-900">Labor</td>
                  <td className="px-4 py-3 text-gray-700">High (tools, disassembly)</td>
                  <td className="px-4 py-3 text-gray-700">Moderate (initiate cycle)</td>
                  <td className="px-4 py-3 text-gray-700">Low (monitor only)</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold text-gray-900">Best For</td>
                  <td className="px-4 py-3 text-gray-700">Low-duty cycle, budget projects</td>
                  <td className="px-4 py-3 text-gray-700">Moderate loading, accessible sites</td>
                  <td className="px-4 py-3 text-gray-700">High solids, 24/7 operations</td>
                </tr>
                <tr className="bg-blue-50">
                  <td className="px-4 py-3 font-semibold text-gray-900">Upgrade Path</td>
                  <td className="px-4 py-3 text-ctc-blue font-semibold">→ Semi-Auto</td>
                  <td className="px-4 py-3 text-ctc-blue font-semibold">→ Fully Auto</td>
                  <td className="px-4 py-3 text-green-600 font-semibold">Optimal setup</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Cleaning Technologies Overview */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">Cleaning Technologies Explained</h2>
          <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
            Learn about the specific technologies that power each cleaning approach
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Link to="/filtration/inline/big/brush-cleaning">
              <div className="bg-white rounded-lg shadow-lg p-8 border-2 border-gray-200 hover:border-green-600 transition-colors cursor-pointer h-full">
                <RotateCw className="text-green-600 mb-4" size={40} />
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Brush Technology</h3>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Rotating brush assemblies mechanically scrub screen surfaces to remove organic matter, algae, and fibrous debris. Available in both manual (BIF) and fully automatic (RAB) configurations.
                </p>
                <div className="flex items-center text-green-600 font-semibold">
                  Learn More About Brush Technology
                  <ArrowRight className="ml-2" size={18} />
                </div>
              </div>
            </Link>

            <Link to="/filtration/inline/big/vacuum-cleaning">
              <div className="bg-white rounded-lg shadow-lg p-8 border-2 border-gray-200 hover:border-ctc-blue transition-colors cursor-pointer h-full">
                <Droplets className="text-ctc-blue mb-4" size={40} />
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Vacuum Technology (MVC & AVC)</h3>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  High-suction vacuum scanner pulls debris through rapid backflush cycles, ideal for heavy solids and sand. Available as semi-automatic MVC (manual) or fully automatic AVC (automated with differential pressure triggers).
                </p>
                <div className="flex items-center text-ctc-blue font-semibold">
                  Learn More About Vacuum Technology
                  <ArrowRight className="ml-2" size={18} />
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Series Applicability Cards */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Compatible Filter Series</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* BIG Series */}
            <div className="bg-gradient-to-br from-blue-50 to-white border-2 border-blue-200 rounded-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">BIG Series</h3>
              <p className="text-sm text-gray-700 mb-4 leading-relaxed">
                Compatible with Manual, BIF, MVC, RAB, and AVC cleaning options.
              </p>
              <div className="space-y-2 mb-4">
                <div className="flex items-center text-sm text-gray-700">
                  <CheckCircle className="text-green-600 mr-2" size={16} />
                  <span>Manual cleaning</span>
                </div>
                <div className="flex items-center text-sm text-gray-700">
                  <CheckCircle className="text-green-600 mr-2" size={16} />
                  <span>BIF & MVC (Semi-Auto)</span>
                </div>
                <div className="flex items-center text-sm text-gray-700">
                  <CheckCircle className="text-green-600 mr-2" size={16} />
                  <span>RAB & AVC (Fully Auto)</span>
                </div>
              </div>
              <Link
                to="/filtration/inline/big"
                className="block w-full bg-ctc-blue text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors text-center text-sm"
              >
                See BIG Series
              </Link>
            </div>

            {/* BIG-S Series */}
            <div className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-300 rounded-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">BIG-S Series</h3>
              <p className="text-sm text-gray-700 mb-4 leading-relaxed">
                Compatible with Manual, BIF, MVC, RAB, and AVC cleaning options.
              </p>
              <div className="space-y-2 mb-4">
                <div className="flex items-center text-sm text-gray-700">
                  <CheckCircle className="text-green-600 mr-2" size={16} />
                  <span>Manual cleaning</span>
                </div>
                <div className="flex items-center text-sm text-gray-700">
                  <CheckCircle className="text-green-600 mr-2" size={16} />
                  <span>BIF & MVC (Semi-Auto)</span>
                </div>
                <div className="flex items-center text-sm text-gray-700">
                  <CheckCircle className="text-green-600 mr-2" size={16} />
                  <span>RAB & AVC (Fully Auto)</span>
                </div>
              </div>
              <Link
                to="/filtration/inline/big-s"
                className="block w-full bg-gray-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-gray-700 transition-colors text-center text-sm"
              >
                See BIG-S Series
              </Link>
            </div>

            {/* GNG Series */}
            <div className="bg-gradient-to-br from-green-50 to-white border-2 border-green-200 rounded-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">GNG Series</h3>
              <p className="text-sm text-gray-700 mb-4 leading-relaxed">
                Compatible with Manual, BIF, MVC, RAB, and AVC cleaning options.
              </p>
              <div className="space-y-2 mb-4">
                <div className="flex items-center text-sm text-gray-700">
                  <CheckCircle className="text-green-600 mr-2" size={16} />
                  <span>Manual cleaning</span>
                </div>
                <div className="flex items-center text-sm text-gray-700">
                  <CheckCircle className="text-green-600 mr-2" size={16} />
                  <span>BIF & MVC (Semi-Auto)</span>
                </div>
                <div className="flex items-center text-sm text-gray-700">
                  <CheckCircle className="text-green-600 mr-2" size={16} />
                  <span>RAB & AVC (Fully Auto)</span>
                </div>
              </div>
              <Link
                to="/filtration/inline/gng"
                className="block w-full bg-green-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-green-700 transition-colors text-center text-sm"
              >
                See GNG Series
              </Link>
            </div>

            {/* TIG Series - NOT Compatible */}
            <div className="bg-gradient-to-br from-orange-50 to-white border-2 border-orange-200 rounded-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">TIG Series</h3>
              <p className="text-sm text-gray-700 mb-4 leading-relaxed">
                Not compatible with Brush/Vacuum/RAB cleaning options.
              </p>
              <div className="space-y-2 mb-4">
                <div className="flex items-center text-sm text-gray-700">
                  <AlertTriangle className="text-orange-600 mr-2" size={16} />
                  <span>Standard maintenance only</span>
                </div>
                <div className="flex items-center text-sm text-gray-700">
                  <AlertTriangle className="text-orange-600 mr-2" size={16} />
                  <span>No BIF/MVC/RAB/AVC support</span>
                </div>
              </div>
              <Link
                to="/filtration/inline/tig"
                className="block w-full bg-orange-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-orange-700 transition-colors text-center text-sm"
              >
                See TIG Series
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-gradient-to-br from-ctc-blue to-blue-800 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Optimize Your Filtration System?</h2>
          <p className="text-xl text-blue-100 mb-8">
            Our filtration specialists can help you select the right cleaning option for your application and operational requirements.
          </p>

          <button
            onClick={() => setIsContactModalOpen(true)}
            className="bg-white text-ctc-blue px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg inline-flex items-center justify-center"
          >
            <span>Talk to a Filtration Specialist</span>
            <ArrowRight className="ml-2" size={20} />
          </button>
        </div>
      </section>

      <Footer />

      <UnifiedContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        productType="filtration"
        formSource="product_quote"
      />
    </div>
  );
};

export default CleaningOptionsPage;
